/**
 * Sidebar Component (Pattern C — single unified panel)
 * 240px panel: logo header, primary nav, system nav, account footer.
 * Replaces AppRail + Section-Sidebar combo on Home / Radar / Network / Database / Settings.
 * AdminLayout still uses SectionSidebar directly.
 */
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '@/store/authStore';
import { useFavourites } from '@/store/favouritesStore';
import { useRehydrateFavourites } from '@/store/useRehydrateFavourites';
import { SidebarGlobalSearch } from '@/components/search';
import { CompanyLogo } from '@/components/ui';
import { WorkspaceSwitcher } from '@/features/workspaces';
import { normalizePersonName } from '@/utils/displayName';
import { NetworkIcon } from './NetworkIcon';
import styles from './Sidebar.module.css';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

const PRIMARY_NAV: SidebarNavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    to: '/radar',
    label: 'Radar',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    to: '/network',
    label: 'Network',
    icon: <NetworkIcon width={18} height={18} />,
  },
  {
    to: '/database',
    label: 'Database',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="6" rx="8" ry="3" />
        <path d="M4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
        <path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" />
      </svg>
    ),
  },
  {
    to: '/crm',
    label: 'Pipeline',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="4" height="18" rx="1" />
        <rect x="10" y="3" width="4" height="13" rx="1" />
        <rect x="17" y="3" width="4" height="8" rx="1" />
      </svg>
    ),
  },
];

const SETTINGS_ITEM: SidebarNavItem = {
  to: '/settings',
  label: 'Settings',
  icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

const ADMIN_ITEM: SidebarNavItem = {
  to: '/admin',
  label: 'Admin',
  icon: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const user = useAuthStore((state) => state.user);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const logout = useAuthStore((state) => state.logout);
  const {
    items: favourites,
    collapsed: favouritesCollapsed,
    toggleCollapsed: toggleFavouritesCollapsed,
    removeFavourite,
  } = useFavourites();

  // Heal company favourites whose stored name predates the backend
  // display_name rollout (rendered verbatim below, so they'd otherwise stay raw).
  useRehydrateFavourites();

  const showAdmin = isAdmin && hasPermission('admin:access');

  const isActive = (to: string) => {
    if (to === '/') return currentPath === '/';
    return currentPath === to || currentPath.startsWith(to + '/');
  };

  const getUserInitials = () => {
    if (!user) return '?';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    return user.email[0].toUpperCase();
  };

  const getUserName = () => {
    if (!user) return '';
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const renderNavItem = (item: SidebarNavItem) => {
    const active = isActive(item.to);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
        onClick={onClose}
      >
        <span className={styles.navIcon}>{item.icon}</span>
        <span className={styles.navLabel}>{item.label}</span>
        {item.badge !== undefined && item.badge > 0 && (
          <span className={styles.badge}>{item.badge}</span>
        )}
      </Link>
    );
  };

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}>
      {/* Header: logo + wordmark */}
      <Link to="/" className={styles.header} aria-label="Home" onClick={onClose}>
        <img src="/logo-icon.svg" alt="" className={styles.headerLogo} />
        <span className={styles.headerWordmark}>Semaphoric</span>
      </Link>

      {/* Workspace switcher */}
      <WorkspaceSwitcher />

      {/* Navigation */}
      <nav className={styles.nav}>
        <SidebarGlobalSearch />
        <div className={styles.group}>
          {PRIMARY_NAV.map(renderNavItem)}
        </div>

        <div className={`${styles.group} ${styles.favouritesGroup}`}>
          <button
            type="button"
            className={styles.favouritesHeader}
            onClick={toggleFavouritesCollapsed}
            aria-expanded={!favouritesCollapsed}
            aria-controls="sidebar-favourites-list"
          >
            <svg
              className={`${styles.favouritesChevron} ${favouritesCollapsed ? styles.favouritesChevronCollapsed : ''}`}
              width="10"
              height="10"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 4.5l3 3 3-3" />
            </svg>
            <span className={styles.favouritesLabel}>Favourites</span>
            {favourites.length > 0 && (
              <span className={styles.favouritesCount}>{favourites.length}</span>
            )}
          </button>
          {!favouritesCollapsed && (
            <div className={styles.favouritesList} id="sidebar-favourites-list">
              {favourites.length === 0 ? (
                <div className={styles.favouritesEmpty}>
                  Star a company or person to pin it here.
                </div>
              ) : (
                favourites.map((f) => {
                  // Company favourites are captured with the backend
                  // display_name already applied (see FavouriteStar call
                  // sites) — re-running the client heuristic on top would
                  // undo deliberate casing (e.g. "JPMorgan" -> "Jpmorgan").
                  const displayName =
                    f.kind === 'company' ? f.name : normalizePersonName(f.name);
                  const sharedBody = (
                    <>
                      <span className={styles.favouriteThumb} aria-hidden>
                        {f.kind === 'company' ? (
                          <CompanyLogo
                            logoUrl={f.logoUrl}
                            domain={f.domain}
                            name={displayName}
                            size={20}
                          />
                        ) : f.photoUrl ? (
                          <img
                            src={f.photoUrl}
                            alt=""
                            className={styles.favouritePhoto}
                            loading="lazy"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className={styles.favouritePhotoFallback}>
                            {favouriteInitials(displayName)}
                          </span>
                        )}
                      </span>
                      <span className={styles.favouriteLabel}>{displayName}</span>
                      <button
                        type="button"
                        className={styles.favouriteRemove}
                        aria-label={`Remove ${displayName} from favourites`}
                        title="Remove from favourites"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeFavourite(f.kind, f.id);
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
                          <path d="M3 3l6 6M9 3l-6 6" />
                        </svg>
                      </button>
                    </>
                  );
                  return f.kind === 'company' ? (
                    <Link
                      key={`c-${f.id}`}
                      to="/database/companies/$companyId"
                      params={{ companyId: f.id }}
                      className={styles.favouriteItem}
                      onClick={onClose}
                    >
                      {sharedBody}
                    </Link>
                  ) : (
                    <Link
                      key={`p-${f.id}`}
                      to="/database/people/$personaId"
                      params={{ personaId: f.id }}
                      className={styles.favouriteItem}
                      onClick={onClose}
                    >
                      {sharedBody}
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div className={styles.group}>
          <div className={styles.groupLabel}>System</div>
          {showAdmin && renderNavItem(ADMIN_ITEM)}
          {renderNavItem(SETTINGS_ITEM)}
        </div>
      </nav>

      {/* Footer: avatar + signout */}
      <div className={styles.footer}>
        <Link to="/settings/account" className={styles.account} aria-label="Account" onClick={onClose}>
          <div className={styles.avatar}>
            {user?.profile_picture_url ? (
              <img src={user.profile_picture_url} alt="" className={styles.avatarImg} />
            ) : (
              <span>{getUserInitials()}</span>
            )}
          </div>
          <div className={styles.accountText}>
            <span className={styles.accountName}>{getUserName()}</span>
            <span className={styles.accountEmail}>{user?.email}</span>
          </div>
        </Link>
        <button
          type="button"
          className={styles.signoutBtn}
          onClick={handleLogout}
          aria-label="Sign out"
          title="Sign out"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </aside>
  );
}

function favouriteInitials(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) return tokens[0].charAt(0).toUpperCase();
  return (tokens[0].charAt(0) + tokens[tokens.length - 1].charAt(0)).toUpperCase();
}
